import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccountHostingList } from '@newfold/huapi-js';
import { UserHosting200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import { useMFEContext } from '~/components/MFEProvider';
import useAMRevamp from '~/hooks/useAMRevamp';
import useHostingInfo from '~/hooks/useHostingInfo';
import { useAddSiteContext } from '~/scenes/AddSetupSiteFlows/context/AddSiteContext';
import { getSelectedHostingId } from '~/utils/hosting/helpers';

import AddSiteValidation from './Validation';

const AddSiteButton = ({
  showingAllSites = false,
  isPending = false,
  children = undefined,
  fullWidth = false,
  disableAddSiteButton = false,
  hostingPlan = undefined,
  icon = undefined,
  variant = 'contained',
  availableSites = undefined,
}: PropsWithChildren<{
  showingAllSites?: boolean;
  isPending?: boolean;
  fullWidth?: boolean;
  disableAddSiteButton?: boolean;
  hostingPlan?: UserHosting200RowsItem;
  availableSites?: number | null;
  icon?: React.ReactNode;
  variant?: 'contained' | 'text' | 'outlined';
}>) => {
  const { t } = useTranslation('sites');

  const [selected, setSelected] = useState(false);
  const [isDBUsageLoading, setIsDBUsageLoading] = useState(false);
  const { isAMRevamp } = useAMRevamp();
  const { clearChosenSiteId, clearOptionSelected, clearFormData } =
    useAddSiteContext();
  const {
    // @ts-expect-error
    updateMFEContext,
  } = useMFEContext();

  const { data: hostingList, isFetching: loadingHostingAccounts } =
    useAccountHostingList(undefined, {
      query: {
        enabled: showingAllSites,
      },
    });
  const id = hostingPlan?.account_info?.id;
  const addon_back_ref = hostingPlan?.account_info?.addon_back_ref;
  const hostingPlanId = getSelectedHostingId(id, addon_back_ref);

  const hostingInfoProps = (() => {
    if (showingAllSites)
      return {
        hostingList: hostingList?.data?.accounts,
      };
    if (isAMRevamp)
      return {
        hostingId: hostingPlanId,
      };
    return undefined;
  })();

  const { isDisabled, allHostingDisabled } = useHostingInfo(hostingInfoProps);

  // handle start validation
  const handleValidation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    clearFormData();
    clearChosenSiteId();
    clearOptionSelected();
    if (isAMRevamp) {
      updateMFEContext(hostingPlanId, hostingPlan);
    }
    return setSelected(true);
  };

  return (
    <>
      <LoadingButtonWrapper
        sx={{ mr: 1 }}
        disabled={
          (showingAllSites ? allHostingDisabled : isDisabled) ||
          isPending ||
          disableAddSiteButton
        }
        loading={isPending || loadingHostingAccounts || isDBUsageLoading}
        data-testid="add-site-btn"
        onClick={handleValidation}
        fullWidth={fullWidth}
        variant={variant}
      >
        {children || icon || t('addSiteBtn')}
      </LoadingButtonWrapper>

      <AddSiteValidation
        open={selected}
        setSelected={setSelected}
        showingAllSites={showingAllSites}
        setIsDBUsageLoading={setIsDBUsageLoading}
        selectedHostingId={hostingPlanId}
        availableSitesToAdd={availableSites}
      />
    </>
  );
};

export default AddSiteButton;
